// Load everything a claim needs and work out both sides (PBS claimed / SWI recommends).
// Shared by the claim page (browser client) and the certificate PDF function (service client) — same rules both places.
import type { SupabaseClient } from '@supabase/supabase-js';
import { addToDate, assess, assessDeposit, lineAmounts, r2, ZERO, type Assessment, type Terms, type ToDate } from './claimCalc';
import { vatOn, type VatChange } from './vat';

export type Contract = {
  id: string;
  site_id: string;
  building_id: string | null;
  contractor_org_id: string | null;
  architect_org_id: string | null;
  contract_no: string | null;
  project_no: string | null;
  title: string | null;
  sum_vep: number | null;
  deposit_pct: number | null;
  retention_pct: number | null;
  employer_name: string | null;
  works_name: string | null;
  cert_code: string | null;
  deposit_recovery_pct: number | null;
  retention_limit_pct: number | null;
  open_after_claim: number | null;
  open_gross_vep: number | null;
  open_variations_vep: number | null;
  open_deposit_recovered: number | null;
  open_retention_held: number | null;
  open_paid_vep: number | null;
  vat_pct?: number | null;
  vat_changes?: VatChange[] | null;
};
export type ClaimDetails = {
  id: string;
  item_id: string;
  contract_id: string | null;
  claim_no: number | null;
  period_to: string | null;
  pbs_total_vip: number | null;
  works_claimed: number | null;
  works_swi: number | null;
  deposit_override: number | null;
  retention_override: number | null;
  /** V0.4.6: this claim is the deposit (advance payment) */
  is_deposit?: boolean | null;
  /** V0.4.6: take retention from the deposit (normally not) */
  deposit_retention?: boolean | null;
  /** V0.4.10: "Issued by" on the certificate (null = architect of record) */
  issuer_org_id?: string | null;
  /** V0.4.10: issued final with SWI's sign-off only */
  swi_only?: boolean | null;
  notes: string | null;
  certified_at: string | null;
  locked_at: string | null;
  cert_ref: string | null;
  paid_on: string | null;
  paid_amount: number | null;
  assessment: { swi?: Assessment; claimed?: Assessment } | null;
  ai_read_at: string | null;
  updated_at: string;
};
export type ClaimLine = {
  id: string;
  sort: number;
  code: string | null;
  description: string;
  contract_value: number | null;
  prev_pct: number | null;
  claimed_pct: number | null;
  swi_pct: number | null;
  note: string | null;
  source: string;
  updated_at: string;
  deleted_at: string | null;
};
export type ClaimVar = {
  id: string;
  sort: number;
  ref_on_claim: string | null;
  description: string | null;
  listed_amount: number | null;
  claimed_now: number | null;
  variation_item_id: string | null;
  match_confirmed: boolean;
  swi_amount: number | null;
  note: string | null;
  source: string;
  updated_at: string;
  deleted_at: string | null;
};
export type RegVar = { id: string; number: string | null; ref_no: string | null; title: string; status: string; amount: number | null; building_ids: string[] };
export type Signature = {
  id: string;
  kind: 'swi' | 'architect';
  method: 'pm_c3' | 'upload';
  user_id: string | null;
  full_name: string;
  role_title: string | null;
  org_name: string | null;
  file_id: string | null;
  signed_at: string;
  revoked_at: string | null;
};

export const APPROVED = new Set(['approved', 'complete']);

export type VarCheck = {
  row: ClaimVar;
  match: RegVar | null;
  suggested: boolean; // match found by building + amount, not yet confirmed
  state: 'ok' | 'close' | 'not_approved' | 'none';
  diff: number; // listed − register amount
  swi: number; // what SWI accepts this period
};

/** Check 2: find the register variation for a row (confirmed link, else same building with amount within 2 %). */
export function checkVariation(row: ClaimVar, reg: RegVar[]): VarCheck {
  let match = row.variation_item_id ? reg.find((r) => r.id === row.variation_item_id) ?? null : null;
  let suggested = false;
  if (!match && row.listed_amount) {
    const ref = (row.ref_on_claim ?? '').replace(/\D/g, '');
    const near = reg
      .filter((r) => r.amount && Math.abs(r.amount - row.listed_amount!) <= Math.abs(row.listed_amount!) * 0.02)
      .sort((a, b) => Math.abs(a.amount! - row.listed_amount!) - Math.abs(b.amount! - row.listed_amount!));
    match = near.find((r) => ref && (r.ref_no ?? r.number ?? '').replace(/\D/g, '').endsWith(ref)) ?? near[0] ?? null;
    suggested = Boolean(match);
  }
  const diff = match && row.listed_amount !== null ? r2(row.listed_amount - (match.amount ?? 0)) : 0;
  let state: VarCheck['state'] = 'none';
  if (match) state = !APPROVED.has(match.status) ? 'not_approved' : diff !== 0 || suggested || !row.match_confirmed ? 'close' : 'ok';
  if (state === 'close' && row.match_confirmed && diff === 0) state = 'ok';
  const claimed = row.claimed_now ?? 0;
  const swi = row.swi_amount !== null ? row.swi_amount : state === 'ok' || (state === 'close' && row.match_confirmed) ? claimed : 0;
  return { row, match, suggested, state, diff, swi: r2(swi) };
}

export type ClaimModel = {
  terms: Terms;
  contract: Contract | null;
  before: ToDate;
  beforeLabel: string;
  approvedVariations: number;
  variationsBefore: number; // approved variations claimed on earlier claims
  lines: ClaimLine[];
  vars: VarCheck[];
  worksClaimed: number;
  worksSwi: number;
  varClaimed: number;
  varSwi: number;
  claimed: Assessment;
  swi: Assessment;
  /** this claim is the deposit (advance payment) */
  isDeposit: boolean;
  /** the contract's deposit: deposit % × contract sum (VEP) */
  depositDefault: number;
};

/** Contract terms; VAT = the contract's rate on `date` (the claim period), else the Fiji rate on that date. */
export function termsOf(c: Contract | null, date?: string | null): Terms {
  return {
    vat_pct: vatOn(c, date),
    sum_vep: Number(c?.sum_vep ?? 0),
    deposit_pct: Number(c?.deposit_pct ?? 0),
    deposit_recovery_pct: Number(c?.deposit_recovery_pct ?? 0),
    retention_pct: Number(c?.retention_pct ?? 0),
    retention_limit_pct: Number(c?.retention_limit_pct ?? c?.retention_pct ?? 0),
  };
}

export function openingOf(c: Contract | null): ToDate {
  return addToDate(ZERO, {
    gross_vep: Number(c?.open_gross_vep ?? 0),
    variations_vep: Number(c?.open_variations_vep ?? 0),
    deposit_recovered: Number(c?.open_deposit_recovered ?? 0),
    retention_held: Number(c?.open_retention_held ?? 0),
    paid_vep: Number(c?.open_paid_vep ?? 0),
  });
}

/** Work out both sides from the loaded pieces (pure — easy to test). */
export function buildModel(p: {
  details: ClaimDetails | null;
  contract: Contract | null;
  lines: ClaimLine[];
  vars: ClaimVar[];
  regVars: RegVar[];
  previous: { claim_no: number | null; assessment: ClaimDetails['assessment'] }[];
}): ClaimModel {
  const terms = termsOf(p.contract, p.details?.period_to ?? null);
  const lines = p.lines.filter((l) => !l.deleted_at).sort((a, b) => a.sort - b.sort);
  const amounts = lines.map(lineAmounts);
  const worksClaimed = lines.length ? r2(amounts.reduce((s, a) => s + a.claimed, 0)) : Number(p.details?.works_claimed ?? 0);
  const worksSwi = lines.length ? r2(amounts.reduce((s, a) => s + a.swi, 0)) : Number(p.details?.works_swi ?? p.details?.works_claimed ?? 0);
  const vars = p.vars.filter((v) => !v.deleted_at).sort((a, b) => a.sort - b.sort).map((v) => checkVariation(v, p.regVars));
  const varClaimed = r2(vars.reduce((s, v) => s + (v.row.claimed_now ?? 0), 0));
  const varSwi = r2(vars.reduce((s, v) => s + v.swi, 0));
  const approvedVariations = r2(p.regVars.filter((r) => APPROVED.has(r.status)).reduce((s, r) => s + (r.amount ?? 0), 0));
  // running totals: the latest earlier certified claim's snapshot, else the contract's opening totals
  const prev = p.previous
    .filter((x) => x.assessment?.swi?.toDate && (x.claim_no ?? 0) < (p.details?.claim_no ?? Infinity))
    .sort((a, b) => (b.claim_no ?? 0) - (a.claim_no ?? 0))[0];
  const usePrev = prev && (prev.claim_no ?? 0) > (p.contract?.open_after_claim ?? -1);
  const before = usePrev ? addToDate(ZERO, prev!.assessment!.swi!.toDate) : openingOf(p.contract);
  const beforeLabel = usePrev
    ? `after certified claim ${prev!.claim_no}`
    : p.contract?.open_after_claim
      ? `after claim ${p.contract.open_after_claim} (opening totals on the contract)`
      : 'no earlier claims recorded';
  const o = { deposit: p.details?.deposit_override ?? null, retention: p.details?.retention_override ?? null };
  const depositDefault = r2((terms.sum_vep * terms.deposit_pct) / 100);
  const isDeposit = Boolean(p.details?.is_deposit);
  if (isDeposit) {
    // the deposit claim: amount = typed amount, else the contract deposit; no lines, variations or recovery
    const depClaimed = p.details?.works_claimed ?? depositDefault;
    const depSwi = p.details?.works_swi ?? depClaimed;
    const ret = Boolean(p.details?.deposit_retention);
    return {
      terms,
      contract: p.contract,
      before,
      beforeLabel,
      approvedVariations,
      variationsBefore: before.variations_vep,
      lines,
      vars,
      worksClaimed: Number(depClaimed),
      worksSwi: Number(depSwi),
      varClaimed: 0,
      varSwi: 0,
      claimed: assessDeposit(terms, Number(depClaimed), before, approvedVariations, { retention: ret }),
      swi: assessDeposit(terms, Number(depSwi), before, approvedVariations, { retention: ret, retentionOverride: o.retention }),
      isDeposit,
      depositDefault,
    };
  }
  return {
    terms,
    contract: p.contract,
    before,
    beforeLabel,
    approvedVariations,
    variationsBefore: before.variations_vep,
    lines,
    vars,
    worksClaimed,
    worksSwi,
    varClaimed,
    varSwi,
    claimed: assess(terms, worksClaimed, varClaimed, before, approvedVariations, { deposit: o.deposit }),
    swi: assess(terms, worksSwi, varSwi, before, approvedVariations, o),
    isDeposit,
    depositDefault,
  };
}

/** Everything for one claim from the database. */
export async function loadClaim(db: SupabaseClient, itemId: string) {
  const [{ data: item }, { data: details }, { data: lines }, { data: vars }, { data: sigs }] = await Promise.all([
    db.from('items').select('*').eq('id', itemId).maybeSingle(),
    db.from('claim_details').select('*').eq('item_id', itemId).is('deleted_at', null).maybeSingle(),
    db.from('claim_lines').select('*').eq('item_id', itemId).is('deleted_at', null).order('sort'),
    db.from('claim_variations').select('*').eq('item_id', itemId).is('deleted_at', null).order('sort'),
    db.from('claim_signatures').select('*').eq('item_id', itemId).is('deleted_at', null).order('signed_at'),
  ]);
  const det = (details ?? null) as ClaimDetails | null;
  let contract: Contract | null = null;
  if (det?.contract_id) contract = ((await db.from('contracts').select('*').eq('id', det.contract_id).maybeSingle()).data ?? null) as Contract | null;
  const buildingId = contract?.building_id ?? (item?.building_ids?.[0] as string | undefined) ?? null;
  const regVars = buildingId
    ? (((
        await db
          .from('items')
          .select('id, number, ref_no, title, status, amount, building_ids')
          .eq('site_id', item?.site_id ?? '')
          .eq('type', 'variation')
          .is('deleted_at', null)
          .contains('building_ids', [buildingId])
      ).data ?? []) as RegVar[])
    : [];
  const previous = contract
    ? (((
        await db.from('claim_details').select('claim_no, assessment').eq('contract_id', contract.id).neq('item_id', itemId).not('locked_at', 'is', null).is('deleted_at', null)
      ).data ?? []) as { claim_no: number | null; assessment: ClaimDetails['assessment'] }[])
    : [];
  const model = buildModel({ details: det, contract, lines: (lines ?? []) as ClaimLine[], vars: (vars ?? []) as ClaimVar[], regVars, previous });
  return { item, details: det, contract, regVars, signatures: ((sigs ?? []) as Signature[]).filter((s) => !s.revoked_at), allSignatures: (sigs ?? []) as Signature[], model };
}
